import { useState, useMemo } from "react";
import AdminLayout from "@/components/layout-admin";
import {
  useCajaBalance,
  useCajaTransactions,
  useAdminDeposits,
  useCreateDeposit,
  useUpdateDeposit,
  useLogCajaPayment,
  useCajaSettle,
  useCajaReconciliation,
  useApartments,
  useAdminUsers,
  useAdminReservations,
} from "@/hooks/use-resources";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  HandCoins,
  Shield,
  Plus,
  RotateCcw,
  Ban,
  CalendarDays,
  FileText,
  Receipt,
  CreditCard,
  Handshake,
} from "lucide-react";

type TransactionFilterType = "all" | "guest_payment" | "admin_fee" | "owner_settlement" | "deposit";

function formatMoney(amount: string | number | null | undefined): string {
  const num = Number(amount || 0);
  return `$${num.toFixed(2)}`;
}

function formatDate(d: string | Date | null | undefined) {
  if (!d) return "N/A";
  return new Date(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function getTypeLabel(type: string): string {
  switch (type) {
    case "admin_fee": return "service charge";
    default: return type.replace(/_/g, " ");
  }
}

function getTypeBadgeClass(type: string) {
  switch (type) {
    case "guest_payment":
    case "income":
      return "bg-green-600 text-white";
    case "admin_fee":
      return "bg-orange-500 text-white";
    case "owner_settlement":
      return "bg-blue-600 text-white";
    case "deposit_in":
      return "bg-purple-600 text-white";
    case "deposit_out":
    case "expense":
      return "bg-red-600 text-white";
    default:
      return "";
  }
}

function getStatusBadgeClass(status: string) {
  switch (status) {
    case "collected": return "bg-blue-600 text-white";
    case "held": return "bg-yellow-500 text-white";
    case "returned": return "bg-green-600 text-white";
    case "partially_returned": return "bg-orange-500 text-white";
    case "forfeited": return "bg-red-600 text-white";
    default: return "";
  }
}

export default function AdminCaja() {
  const { data: balance, isLoading: balanceLoading } = useCajaBalance();
  const { data: transactions = [], isLoading: transactionsLoading } = useCajaTransactions();
  const { data: deposits = [], isLoading: depositsLoading } = useAdminDeposits();
  const { data: apartments = [] } = useApartments();
  const { data: usersData = [] } = useAdminUsers();
  const { data: reservationsData = [] } = useAdminReservations();
  const logPayment = useLogCajaPayment();
  const settleOwner = useCajaSettle();
  const createDeposit = useCreateDeposit();
  const updateDeposit = useUpdateDeposit();
  const { toast } = useToast();

  const getWeekRange = () => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - dayOfWeek);
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);
    return {
      start: startOfWeek.toISOString().split("T")[0],
      end: endOfWeek.toISOString().split("T")[0],
    };
  };
  const defaultRange = getWeekRange();
  const [reconStart, setReconStart] = useState(defaultRange.start);
  const [reconEnd, setReconEnd] = useState(defaultRange.end);
  const { data: reconciliation, isLoading: reconLoading } = useCajaReconciliation(reconStart, reconEnd);

  const [txFilter, setTxFilter] = useState<TransactionFilterType>("all");

  const [settleDialogOpen, setSettleDialogOpen] = useState(false);
  const [settleOwnerData, setSettleOwnerData] = useState<{ ownerUserId: number; ownerName: string; netOwed: string } | null>(null);
  const [settleAmount, setSettleAmount] = useState("");
  const [settleMethod, setSettleMethod] = useState("cash");
  const [settleDescription, setSettleDescription] = useState("");

  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnDepositId, setReturnDepositId] = useState<number | null>(null);
  const [returnAmount, setReturnAmount] = useState("");
  const [returnType, setReturnType] = useState<"full" | "partial">("full");

  const typedApartments = apartments as Array<{ id: number; name: string; ownerUserId?: number | null }>;
  const typedUsers = usersData as Array<{ id: number; username: string; fullName?: string; role: string }>;
  const typedReservations = reservationsData as Array<{ id: number; guestName: string; apartmentId: number }>;
  const typedTransactions = transactions as Array<{
    id: number; type: string; category: string; amount: string;
    paymentMethod: string | null; description: string | null;
    createdAt: string | null; apartmentId: number | null; ownerUserId: number | null;
    apartmentName?: string; ownerName?: string;
  }>;
  const typedDeposits = deposits as Array<{
    id: number; organizationId: number | null; reservationId: number | null;
    apartmentId: number; ownerUserId: number | null; guestName: string | null;
    amount: string; status: string; returnedAmount: string | null;
    paymentMethod: string | null; notes: string | null;
    collectedAt: string | null; returnedAt: string | null; createdAt: string | null;
  }>;
  const owners = typedUsers.filter((u) => u.role === "owner");

  const [cashPaymentForm, setCashPaymentForm] = useState({
    apartmentId: "",
    reservationId: "",
    amount: "",
    paymentMethod: "cash",
    description: "",
  });

  const [expenseForm, setExpenseForm] = useState({
    type: "admin_fee" as "admin_fee" | "expense",
    category: "check_in_fee",
    amount: "",
    apartmentId: "",
    description: "",
  });

  const [depositForm, setDepositForm] = useState({
    apartmentId: "",
    guestName: "",
    amount: "",
    paymentMethod: "cash",
    reservationId: "",
    notes: "",
  });

  const selectedCashPaymentApartment = typedApartments.find(
    (a) => String(a.id) === cashPaymentForm.apartmentId
  );
  const cashPaymentOwner = selectedCashPaymentApartment
    ? typedUsers.find((u) => u.id === selectedCashPaymentApartment.ownerUserId)
    : null;
  const filteredReservations = typedReservations.filter(
    (r) => String(r.apartmentId) === cashPaymentForm.apartmentId
  );

  const getApartmentName = (apartmentId: number | null | undefined) => {
    if (!apartmentId) return "N/A";
    const apt = typedApartments.find((a) => a.id === apartmentId);
    return apt?.name || `Apt #${apartmentId}`;
  };

  const getOwnerName = (ownerUserId: number | null | undefined) => {
    if (!ownerUserId) return "N/A";
    const user = typedUsers.find((u) => u.id === ownerUserId);
    return user?.fullName || user?.username || `Property Manager #${ownerUserId}`;
  };

  const filteredTransactions = useMemo(() => {
    return typedTransactions
      .filter((t) => {
        if (txFilter === "all") return true;
        if (txFilter === "deposit") return t.type === "deposit_in" || t.type === "deposit_out";
        return t.type === txFilter;
      })
      .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
  }, [typedTransactions, txFilter]);

  const handleRecordCashPayment = async () => {
    if (!cashPaymentForm.apartmentId) {
      toast({ title: "Error", description: "Please select an apartment", variant: "destructive" });
      return;
    }
    if (!cashPaymentForm.amount || Number(cashPaymentForm.amount) <= 0) {
      toast({ title: "Error", description: "Please enter a valid amount", variant: "destructive" });
      return;
    }
    try {
      await logPayment.mutateAsync({
        type: "guest_payment",
        category: "reservation_payment",
        amount: cashPaymentForm.amount,
        paymentMethod: cashPaymentForm.paymentMethod as any,
        apartmentId: Number(cashPaymentForm.apartmentId),
        reservationId: cashPaymentForm.reservationId ? Number(cashPaymentForm.reservationId) : undefined,
        ownerUserId: selectedCashPaymentApartment?.ownerUserId || undefined,
        description: cashPaymentForm.description || undefined,
      });
      setCashPaymentForm({ apartmentId: "", reservationId: "", amount: "", paymentMethod: "cash", description: "" });
      toast({ title: "Cash payment recorded" });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleRecordExpense = async () => {
    if (!expenseForm.amount || Number(expenseForm.amount) <= 0) {
      toast({ title: "Error", description: "Please enter a valid amount", variant: "destructive" });
      return;
    }
    try {
      await logPayment.mutateAsync({
        type: expenseForm.type,
        category: expenseForm.category,
        amount: expenseForm.amount,
        paymentMethod: "cash",
        apartmentId: expenseForm.apartmentId ? Number(expenseForm.apartmentId) : undefined,
        description: expenseForm.description || undefined,
      });
      setExpenseForm({ type: "admin_fee", category: "check_in_fee", amount: "", apartmentId: "", description: "" });
      toast({ title: "Expense recorded" });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleCreateDeposit = async () => {
    if (!depositForm.apartmentId) {
      toast({ title: "Error", description: "Please select an apartment", variant: "destructive" });
      return;
    }
    if (!depositForm.amount || Number(depositForm.amount) <= 0) {
      toast({ title: "Error", description: "Please enter a valid amount", variant: "destructive" });
      return;
    }
    try {
      await createDeposit.mutateAsync({
        apartmentId: Number(depositForm.apartmentId),
        guestName: depositForm.guestName || undefined,
        amount: depositForm.amount,
        paymentMethod: depositForm.paymentMethod as any,
        reservationId: depositForm.reservationId ? Number(depositForm.reservationId) : undefined,
        notes: depositForm.notes || undefined,
      });
      setDepositForm({ apartmentId: "", guestName: "", amount: "", paymentMethod: "cash", reservationId: "", notes: "" });
      toast({ title: "Deposit recorded" });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const openReturnDialog = (depositId: number, type: "full" | "partial") => {
    const dep = typedDeposits.find((d) => d.id === depositId);
    setReturnDepositId(depositId);
    setReturnType(type);
    setReturnAmount(type === "full" ? (dep?.amount || "0") : "");
    setReturnDialogOpen(true);
  };

  const handleReturnDeposit = async () => {
    if (!returnDepositId) return;
    if (!returnAmount || Number(returnAmount) <= 0) {
      toast({ title: "Error", description: "Please enter a valid amount", variant: "destructive" });
      return;
    }
    const dep = typedDeposits.find((d) => d.id === returnDepositId);
    const isFullReturn = Number(returnAmount) >= Number(dep?.amount || 0);
    try {
      await updateDeposit.mutateAsync({
        id: returnDepositId,
        data: {
          status: isFullReturn ? "returned" : "partially_returned",
          returnedAmount: returnAmount,
        },
      });
      setReturnDialogOpen(false);
      setReturnDepositId(null);
      setReturnAmount("");
      toast({ title: isFullReturn ? "Deposit fully returned" : "Deposit partially returned" });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleForfeitDeposit = async (depositId: number) => {
    try {
      await updateDeposit.mutateAsync({
        id: depositId,
        data: { status: "forfeited" },
      });
      toast({ title: "Deposit forfeited" });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const openSettleDialog = (owner: any) => {
    setSettleOwnerData({
      ownerUserId: owner.ownerUserId,
      ownerName: owner.ownerName || `Property Manager #${owner.ownerUserId}`,
      netOwed: owner.netOwed,
    });
    setSettleAmount(String(Number(owner.netOwed || 0).toFixed(2)));
    setSettleMethod("cash");
    setSettleDescription("");
    setSettleDialogOpen(true);
  };

  const handleSettle = async () => {
    if (!settleOwnerData) return;
    if (!settleAmount || Number(settleAmount) <= 0) {
      toast({ title: "Error", description: "Please enter a valid amount", variant: "destructive" });
      return;
    }
    try {
      await settleOwner.mutateAsync({
        ownerUserId: settleOwnerData.ownerUserId,
        amount: settleAmount,
        paymentMethod: settleMethod as any,
        description: settleDescription || undefined,
        periodStart: reconStart,
        periodEnd: reconEnd,
      });
      setSettleDialogOpen(false);
      setSettleOwnerData(null);
      toast({ title: "Settlement recorded" });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const isLoading = balanceLoading || transactionsLoading || depositsLoading;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-2xl font-bold" data-testid="text-caja-title">
            Caja (Cash Box)
          </h1>
          <p className="text-muted-foreground">
            Physical cash tracking, property manager settlements & deposits
          </p>
        </div>

        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground" data-testid="text-loading">
            Loading caja data...
          </div>
        ) : (
          <Tabs defaultValue="cashbox">
            <TabsList data-testid="tabs-caja">
              <TabsTrigger value="cashbox" data-testid="tab-cashbox">Cash Box</TabsTrigger>
              <TabsTrigger value="actions" data-testid="tab-actions">Actions</TabsTrigger>
              <TabsTrigger value="reconciliation" data-testid="tab-reconciliation">Reconciliation & Settle</TabsTrigger>
            </TabsList>

            <TabsContent value="cashbox" className="mt-4 space-y-4">
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <Card className="col-span-2 lg:col-span-1">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Wallet className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Cash In Box</span>
                    </div>
                    <p className="text-3xl font-bold" data-testid="text-current-balance">
                      {formatMoney(balance?.currentBalance)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">{balance?.transactionCount || 0} transactions</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Cash Received</span>
                    </div>
                    <p className="text-2xl font-bold" data-testid="text-cash-in">
                      {formatMoney(balance?.cashIn)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingDown className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Service Charges</span>
                    </div>
                    <p className="text-2xl font-bold" data-testid="text-service-charges">
                      {formatMoney(balance?.serviceCharges)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <HandCoins className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Settlements</span>
                    </div>
                    <p className="text-2xl font-bold" data-testid="text-settlements">
                      {formatMoney(balance?.settlements)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Shield className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Deposits Held</span>
                    </div>
                    <p className="text-2xl font-bold" data-testid="text-deposits-held">
                      {formatMoney(balance?.depositsHeld)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                  <CardTitle className="text-base font-display">Transaction Log</CardTitle>
                  <Select value={txFilter} onValueChange={(v) => setTxFilter(v as TransactionFilterType)}>
                    <SelectTrigger className="w-[180px]" data-testid="select-tx-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="guest_payment">Guest Payments</SelectItem>
                      <SelectItem value="admin_fee">Service Charges</SelectItem>
                      <SelectItem value="owner_settlement">Settlements</SelectItem>
                      <SelectItem value="deposit">Deposits</SelectItem>
                    </SelectContent>
                  </Select>
                </CardHeader>
                <CardContent>
                  {filteredTransactions.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4" data-testid="text-no-transactions">
                      No transactions found
                    </p>
                  ) : (
                    <div className="space-y-1">
                      {filteredTransactions.map((tx) => (
                        <div
                          key={tx.id}
                          className="flex items-center justify-between gap-3 py-2 border-b last:border-b-0 flex-wrap"
                          data-testid={`row-transaction-${tx.id}`}
                        >
                          <div className="flex items-center gap-3 flex-wrap min-w-0">
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {formatDate(tx.createdAt)}
                            </span>
                            <Badge variant="default" className={getTypeBadgeClass(tx.type)}>
                              {getTypeLabel(tx.type)}
                            </Badge>
                            <span className="text-sm">
                              {tx.apartmentName || getApartmentName(tx.apartmentId)}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {tx.ownerName || getOwnerName(tx.ownerUserId)}
                            </span>
                            {tx.description && (
                              <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                                {tx.description}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 flex-wrap">
                            {tx.paymentMethod && (
                              <span className="text-xs text-muted-foreground">
                                {tx.paymentMethod.replace(/_/g, " ")}
                              </span>
                            )}
                            <span className="font-semibold whitespace-nowrap" data-testid={`text-tx-amount-${tx.id}`}>
                              {tx.type === "expense" || tx.type === "admin_fee" || tx.type === "owner_settlement" || tx.type === "deposit_out"
                                ? `-${formatMoney(tx.amount)}`
                                : formatMoney(tx.amount)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="actions" className="mt-4">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base font-display flex items-center gap-2">
                      <Receipt className="w-4 h-4" />
                      Record Cash Payment
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label>Apartment *</Label>
                      <Select
                        value={cashPaymentForm.apartmentId}
                        onValueChange={(v) => setCashPaymentForm((p) => ({ ...p, apartmentId: v, reservationId: "" }))}
                      >
                        <SelectTrigger data-testid="select-cash-apartment">
                          <SelectValue placeholder="Select apartment" />
                        </SelectTrigger>
                        <SelectContent>
                          {typedApartments.map((a) => (
                            <SelectItem key={a.id} value={String(a.id)}>
                              {a.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {cashPaymentOwner && (
                      <div className="text-sm text-muted-foreground" data-testid="text-cash-payment-owner">
                        Owner: {cashPaymentOwner.fullName || cashPaymentOwner.username}
                      </div>
                    )}
                    <div>
                      <Label>Reservation (optional)</Label>
                      <Select
                        value={cashPaymentForm.reservationId}
                        onValueChange={(v) => setCashPaymentForm((p) => ({ ...p, reservationId: v }))}
                      >
                        <SelectTrigger data-testid="select-cash-reservation">
                          <SelectValue placeholder="Select reservation" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {filteredReservations.map((r) => (
                            <SelectItem key={r.id} value={String(r.id)}>
                              {r.guestName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Amount ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={cashPaymentForm.amount}
                        onChange={(e) => setCashPaymentForm((p) => ({ ...p, amount: e.target.value }))}
                        placeholder="0.00"
                        data-testid="input-cash-amount"
                      />
                    </div>
                    <div>
                      <Label>Payment Method</Label>
                      <Select
                        value={cashPaymentForm.paymentMethod}
                        onValueChange={(v) => setCashPaymentForm((p) => ({ ...p, paymentMethod: v }))}
                      >
                        <SelectTrigger data-testid="select-cash-method">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                          <SelectItem value="zelle">Zelle</SelectItem>
                          <SelectItem value="venmo">Venmo</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Description (optional)</Label>
                      <Input
                        value={cashPaymentForm.description}
                        onChange={(e) => setCashPaymentForm((p) => ({ ...p, description: e.target.value }))}
                        placeholder="Add a note..."
                        data-testid="input-cash-description"
                      />
                    </div>
                    <Button
                      className="w-full"
                      onClick={handleRecordCashPayment}
                      disabled={logPayment.isPending}
                      data-testid="button-submit-cash-payment"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {logPayment.isPending ? "Recording..." : "Record Payment"}
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base font-display flex items-center gap-2">
                      <CreditCard className="w-4 h-4" />
                      Record Expense
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label>Type</Label>
                      <Select
                        value={expenseForm.type}
                        onValueChange={(v) => setExpenseForm((p) => ({ ...p, type: v as "admin_fee" | "expense" }))}
                      >
                        <SelectTrigger data-testid="select-expense-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin_fee">Service Charge</SelectItem>
                          <SelectItem value="expense">Expense</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Select
                        value={expenseForm.category}
                        onValueChange={(v) => setExpenseForm((p) => ({ ...p, category: v }))}
                      >
                        <SelectTrigger data-testid="select-expense-category">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="check_in_fee">Check-in Fee</SelectItem>
                          <SelectItem value="cleaning_fee">Cleaning Fee</SelectItem>
                          <SelectItem value="maintenance">Maintenance</SelectItem>
                          <SelectItem value="cleaner_payment">Cleaner Payment</SelectItem>
                          <SelectItem value="miscellaneous">Miscellaneous</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Amount ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={expenseForm.amount}
                        onChange={(e) => setExpenseForm((p) => ({ ...p, amount: e.target.value }))}
                        placeholder="0.00"
                        data-testid="input-expense-amount"
                      />
                    </div>
                    <div>
                      <Label>Apartment (optional)</Label>
                      <Select
                        value={expenseForm.apartmentId}
                        onValueChange={(v) => setExpenseForm((p) => ({ ...p, apartmentId: v }))}
                      >
                        <SelectTrigger data-testid="select-expense-apartment">
                          <SelectValue placeholder="Select apartment" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {typedApartments.map((a) => (
                            <SelectItem key={a.id} value={String(a.id)}>
                              {a.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Description (optional)</Label>
                      <Input
                        value={expenseForm.description}
                        onChange={(e) => setExpenseForm((p) => ({ ...p, description: e.target.value }))}
                        placeholder="Add a note..."
                        data-testid="input-expense-description"
                      />
                    </div>
                    <Button
                      className="w-full"
                      onClick={handleRecordExpense}
                      disabled={logPayment.isPending}
                      data-testid="button-submit-expense"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {logPayment.isPending ? "Recording..." : "Record Expense"}
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base font-display flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Security Deposits
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label>Apartment *</Label>
                      <Select
                        value={depositForm.apartmentId}
                        onValueChange={(v) => setDepositForm((p) => ({ ...p, apartmentId: v }))}
                      >
                        <SelectTrigger data-testid="select-deposit-apartment">
                          <SelectValue placeholder="Select apartment" />
                        </SelectTrigger>
                        <SelectContent>
                          {typedApartments.map((a) => (
                            <SelectItem key={a.id} value={String(a.id)}>
                              {a.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Guest Name</Label>
                      <Input
                        value={depositForm.guestName}
                        onChange={(e) => setDepositForm((p) => ({ ...p, guestName: e.target.value }))}
                        placeholder="Guest name"
                        data-testid="input-deposit-guest"
                      />
                    </div>
                    <div>
                      <Label>Amount ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={depositForm.amount}
                        onChange={(e) => setDepositForm((p) => ({ ...p, amount: e.target.value }))}
                        placeholder="0.00"
                        data-testid="input-deposit-amount"
                      />
                    </div>
                    <div>
                      <Label>Payment Method</Label>
                      <Select
                        value={depositForm.paymentMethod}
                        onValueChange={(v) => setDepositForm((p) => ({ ...p, paymentMethod: v }))}
                      >
                        <SelectTrigger data-testid="select-deposit-method">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                          <SelectItem value="zelle">Zelle</SelectItem>
                          <SelectItem value="venmo">Venmo</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Notes (optional)</Label>
                      <Input
                        value={depositForm.notes}
                        onChange={(e) => setDepositForm((p) => ({ ...p, notes: e.target.value }))}
                        placeholder="Add notes..."
                        data-testid="input-deposit-notes"
                      />
                    </div>
                    <Button
                      className="w-full"
                      onClick={handleCreateDeposit}
                      disabled={createDeposit.isPending}
                      data-testid="button-submit-deposit"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {createDeposit.isPending ? "Recording..." : "Record Deposit"}
                    </Button>

                    {typedDeposits.filter((d) => d.status === "collected" || d.status === "held").length > 0 && (
                      <>
                        <Separator />
                        <p className="text-sm font-medium">Active Deposits</p>
                        <div className="space-y-2">
                          {typedDeposits
                            .filter((d) => d.status === "collected" || d.status === "held")
                            .map((dep) => (
                              <div
                                key={dep.id}
                                className="flex items-center justify-between gap-2 py-2 border-b last:border-b-0 flex-wrap"
                                data-testid={`row-deposit-${dep.id}`}
                              >
                                <div className="min-w-0">
                                  <p className="text-sm font-medium" data-testid={`text-deposit-guest-${dep.id}`}>
                                    {dep.guestName || "Unknown Guest"}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {getApartmentName(dep.apartmentId)} - {formatMoney(dep.amount)}
                                  </p>
                                </div>
                                <div className="flex items-center gap-1 flex-wrap">
                                  <Badge variant="default" className={getStatusBadgeClass(dep.status)}>
                                    {dep.status.replace(/_/g, " ")}
                                  </Badge>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openReturnDialog(dep.id, "full")}
                                    data-testid={`button-return-full-${dep.id}`}
                                  >
                                    <RotateCcw className="w-3 h-3 mr-1" />
                                    Return
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openReturnDialog(dep.id, "partial")}
                                    data-testid={`button-return-partial-${dep.id}`}
                                  >
                                    Partial
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleForfeitDeposit(dep.id)}
                                    data-testid={`button-forfeit-${dep.id}`}
                                  >
                                    <Ban className="w-3 h-3 mr-1" />
                                    Forfeit
                                  </Button>
                                </div>
                              </div>
                            ))}
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="reconciliation" className="mt-4 space-y-4">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-1">
                  <Label className="text-sm whitespace-nowrap">From</Label>
                  <Input
                    type="date"
                    value={reconStart}
                    onChange={(e) => setReconStart(e.target.value)}
                    className="w-auto"
                    data-testid="input-recon-start"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <Label className="text-sm whitespace-nowrap">To</Label>
                  <Input
                    type="date"
                    value={reconEnd}
                    onChange={(e) => setReconEnd(e.target.value)}
                    className="w-auto"
                    data-testid="input-recon-end"
                  />
                </div>
              </div>

              {reconLoading ? (
                <div className="text-center py-8 text-muted-foreground" data-testid="text-recon-loading">Loading reconciliation...</div>
              ) : !reconciliation ? (
                <div className="text-center py-8 text-muted-foreground" data-testid="text-recon-empty">No data for selected period</div>
              ) : (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-xs text-muted-foreground mb-1">Total Cash In</div>
                        <div className="text-2xl font-bold" data-testid="text-recon-cash-in">
                          {formatMoney(reconciliation.summary?.totalCashIn)}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-xs text-muted-foreground mb-1">Service Charges</div>
                        <div className="text-2xl font-bold" data-testid="text-recon-service-charges">
                          {formatMoney(reconciliation.summary?.totalServiceCharges)}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-xs text-muted-foreground mb-1">Settlements</div>
                        <div className="text-2xl font-bold" data-testid="text-recon-settlements">
                          {formatMoney(reconciliation.summary?.totalSettlements)}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-xs text-muted-foreground mb-1">Net In Box</div>
                        <div className="text-2xl font-bold" data-testid="text-recon-net">
                          {formatMoney(reconciliation.summary?.netInBox)}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {reconciliation.ownerBreakdown && reconciliation.ownerBreakdown.length > 0 && (
                    <div className="space-y-3">
                      <h3 className="font-display font-semibold text-lg">Per-Property Manager Breakdown</h3>
                      {reconciliation.ownerBreakdown.map((owner: any, idx: number) => (
                        <Card key={owner.ownerUserId || idx}>
                          <CardContent className="p-4 space-y-3">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <div>
                                <p className="font-semibold" data-testid={`text-recon-owner-${idx}`}>
                                  {owner.ownerName || `Property Manager #${owner.ownerUserId}`}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {owner.apartmentCount || 0} apartment{(owner.apartmentCount || 0) !== 1 ? "s" : ""}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge className="bg-green-600 text-white" data-testid={`badge-recon-owner-net-${idx}`}>
                                  Net Owed: {formatMoney(owner.netOwed)}
                                </Badge>
                                {Number(owner.netOwed) > 0 && (
                                  <Button
                                    size="sm"
                                    onClick={() => openSettleDialog(owner)}
                                    data-testid={`button-settle-${idx}`}
                                  >
                                    <Handshake className="w-4 h-4 mr-1" />
                                    Settle
                                  </Button>
                                )}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-sm">
                              <div>
                                <span className="text-muted-foreground">Cash Collected: </span>
                                <span className="font-medium">{formatMoney(owner.cashCollected)}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Check-in Fees: </span>
                                <span className="font-medium">{formatMoney(owner.serviceCharges?.checkIn)}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Cleaning Fees: </span>
                                <span className="font-medium">{formatMoney(owner.serviceCharges?.cleaning)}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Maintenance: </span>
                                <span className="font-medium">{formatMoney(owner.serviceCharges?.maintenance)}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Misc Fees: </span>
                                <span className="font-medium">{formatMoney(owner.serviceCharges?.misc)}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Total Service Charges: </span>
                                <span className="font-medium">{formatMoney(owner.serviceCharges?.total)}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Deposits Held: </span>
                                <span className="font-medium">{formatMoney(owner.depositsHeld)}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Settlements: </span>
                                <span className="font-medium">{formatMoney(owner.settlements)}</span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}

                  {reconciliation.cashByMethod && Object.keys(reconciliation.cashByMethod).length > 0 && (
                    <div>
                      <h3 className="font-display font-semibold text-lg mb-2">Cash by Payment Method</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {Object.entries(reconciliation.cashByMethod).map(([method, amount]: [string, any]) => (
                          <Card key={method}>
                            <CardContent className="p-4">
                              <div className="text-xs text-muted-foreground capitalize">{method.replace(/_/g, " ")}</div>
                              <div className="text-lg font-semibold" data-testid={`text-recon-method-${method}`}>{formatMoney(amount)}</div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>

      <Dialog open={returnDialogOpen} onOpenChange={setReturnDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{returnType === "full" ? "Return Full Deposit" : "Partial Return"}</DialogTitle>
            <DialogDescription>
              Enter the amount to return to the guest.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Return Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={returnAmount}
                onChange={(e) => setReturnAmount(e.target.value)}
                placeholder="0.00"
                data-testid="input-return-amount"
              />
            </div>
            <div className="flex gap-2 justify-end flex-wrap">
              <Button variant="outline" onClick={() => setReturnDialogOpen(false)} data-testid="button-cancel-return">
                Cancel
              </Button>
              <Button
                onClick={handleReturnDeposit}
                disabled={updateDeposit.isPending}
                data-testid="button-confirm-return"
              >
                {updateDeposit.isPending ? "Processing..." : "Confirm Return"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={settleDialogOpen} onOpenChange={setSettleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Settle with {settleOwnerData?.ownerName}</DialogTitle>
            <DialogDescription>
              Confirm the settlement amount and payment method for this property manager.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">
              Net owed: <span className="font-semibold text-foreground">{formatMoney(settleOwnerData?.netOwed)}</span>
            </div>
            <div>
              <Label>Settlement Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={settleAmount}
                onChange={(e) => setSettleAmount(e.target.value)}
                placeholder="0.00"
                data-testid="input-settle-amount"
              />
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select value={settleMethod} onValueChange={setSettleMethod}>
                <SelectTrigger data-testid="select-settle-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="zelle">Zelle</SelectItem>
                  <SelectItem value="venmo">Venmo</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input
                value={settleDescription}
                onChange={(e) => setSettleDescription(e.target.value)}
                placeholder="Settlement notes..."
                data-testid="input-settle-description"
              />
            </div>
            <div className="flex gap-2 justify-end flex-wrap">
              <Button variant="outline" onClick={() => setSettleDialogOpen(false)} data-testid="button-cancel-settle">
                Cancel
              </Button>
              <Button
                onClick={handleSettle}
                disabled={settleOwner.isPending}
                data-testid="button-confirm-settle"
              >
                {settleOwner.isPending ? "Processing..." : "Confirm Settlement"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
